import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Pencil, ShieldAlert, TriangleAlert } from 'lucide-react';
import { useState, type FormEvent } from 'react';
import { Link } from 'react-router';
import { api, errorMessage } from '../lib/api';
import { useAuth } from '../lib/auth';
import type { PersonFields } from '../lib/checkouts';
import { formatUtcDate } from '../lib/format';
import { useToast } from './toast';
import { Alert, Button, Card, CardHeader, cn, Field, Input, Modal, Select } from './ui';

type FieldWithValue = PersonFields['fields'][number];

const usePersonFields = (personId: string) =>
  useQuery({ queryKey: ['person-fields', personId], queryFn: () => api.get<PersonFields>(`/people/${personId}/fields`) });

function shownValue(field: FieldWithValue) {
  if (field.value === null) return field.type === 'CHECKBOX' ? 'No' : null;
  if (field.type === 'CHECKBOX') return 'Yes';
  if (field.type === 'DATE') return formatUtcDate(`${field.value}T00:00:00.000Z`);
  return field.value;
}

/** For technicians and administrators: the alerts on a person, at the top of their page. */
export function PersonAlertsBanner({ personId }: { personId: string }) {
  const { can } = useAuth();
  const query = usePersonFields(personId);
  const alerts = query.data?.alerts ?? [];
  if (!can('TECHNICIAN') || alerts.length === 0) return null;
  const blocking = alerts.some((alert) => alert.level === 'BLOCK');

  return (
    <Alert
      tone={blocking ? 'red' : 'amber'}
      icon={blocking ? <ShieldAlert /> : <TriangleAlert />}
      title={blocking ? 'Can’t borrow devices unless an administrator overrides it' : 'Check these before lending a device'}
      className="mb-6"
    >
      <ul className="space-y-0.5">
        {alerts.map((alert) => (
          <li key={alert.key}>
            {alert.title}
            {alert.detail && <span className="opacity-75"> · {alert.detail}</span>}
          </li>
        ))}
      </ul>
    </Alert>
  );
}

/** The fields a school keeps on a person, like "Do not lend" or "Insurance expires". */
export function PersonFieldsCard({ personId }: { personId: string }) {
  const { can } = useAuth();
  const [editing, setEditing] = useState(false);
  const query = usePersonFields(personId);
  if (!query.data) return null;
  const { fields, alerts } = query.data;
  if (fields.length === 0 && !can('ADMIN')) return null;
  const levels = new Map(alerts.map((alert) => [alert.key, alert.level]));

  return (
    <Card>
      <CardHeader
        title="Custom fields"
        description="Checked when devices are lent"
        actions={
          can('TECHNICIAN') &&
          fields.some((field) => !field.archived) && (
            <Button size="sm" variant="ghost" icon={<Pencil />} onClick={() => setEditing(true)}>
              Edit
            </Button>
          )
        }
      />
      {fields.length === 0 ? (
        <p className="px-5 py-4 text-sm text-slate-500">
          No fields yet.{' '}
          <Link to="/settings?tab=checkouts" className="font-medium text-brand-700 hover:underline">
            Add fields
          </Link>{' '}
          like “Do not lend” or “Insurance expires.”
        </p>
      ) : (
        <dl className="divide-y divide-slate-100">
          {fields.map((field) => {
            const level = levels.get(`field:${field.id}`);
            const value = shownValue(field);
            return (
              <div key={field.id} className="flex items-baseline justify-between gap-3 px-5 py-2.5 text-sm">
                <dt className="text-slate-500">{field.name}</dt>
                <dd className={cn('text-right font-medium', level === 'BLOCK' ? 'text-red-700' : level === 'WARN' ? 'text-amber-700' : 'text-slate-900')}>
                  {level && <TriangleAlert className="mr-1 inline size-3.5 -translate-y-px" />}
                  {value ?? <span className="font-normal text-slate-400">—</span>}
                </dd>
              </div>
            );
          })}
        </dl>
      )}
      {editing && <PersonFieldsModal personId={personId} fields={fields} onClose={() => setEditing(false)} />}
    </Card>
  );
}

function PersonFieldsModal({ personId, fields, onClose }: { personId: string; fields: FieldWithValue[]; onClose(): void }) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const editable = fields.filter((field) => !field.archived);
  const [values, setValues] = useState<Record<string, string | boolean>>(() =>
    Object.fromEntries(editable.map((field) => [field.id, field.type === 'CHECKBOX' ? field.value === 'true' : (field.value ?? '')])),
  );

  const save = useMutation({
    mutationFn: () =>
      api.put<PersonFields>(`/people/${personId}/fields`, {
        values: Object.fromEntries(
          editable.map((field) => {
            const value = values[field.id];
            return [field.id, typeof value === 'string' && !value.trim() ? null : value];
          }),
        ),
      }),
    onSuccess: (data) => {
      queryClient.setQueryData(['person-fields', personId], data);
      void queryClient.invalidateQueries({ queryKey: ['borrower-alerts'] });
      toast.success('Fields saved');
      onClose();
    },
    onError: (err) => toast.error(errorMessage(err)),
  });

  const submit = (event: FormEvent) => {
    event.preventDefault();
    save.mutate();
  };

  return (
    <Modal
      open
      onClose={onClose}
      title="Custom fields"
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button type="submit" form="person-fields" variant="primary" loading={save.isPending}>
            Save
          </Button>
        </>
      }
    >
      <form id="person-fields" onSubmit={submit} className="space-y-4">
        {editable.map((field) => {
          const value = values[field.id];
          const set = (next: string | boolean) => setValues((current) => ({ ...current, [field.id]: next }));
          if (field.type === 'CHECKBOX') {
            return (
              <label key={field.id} className="flex items-start gap-2 text-sm">
                <input type="checkbox" checked={value === true} onChange={(event) => set(event.target.checked)} className="mt-0.5 size-4 rounded border-slate-300" />
                <span>
                  <span className="font-medium text-slate-800">{field.name}</span>
                  {field.description && <span className="block text-xs text-slate-500">{field.description}</span>}
                </span>
              </label>
            );
          }
          return (
            <Field key={field.id} label={field.name} hint={field.description ?? undefined}>
              {field.type === 'SELECT' ? (
                <Select value={String(value ?? '')} onChange={(event) => set(event.target.value)}>
                  <option value="">Not set</option>
                  {field.options.map((option) => (
                    <option key={option} value={option}>
                      {option}
                    </option>
                  ))}
                </Select>
              ) : (
                <Input
                  type={field.type === 'DATE' ? 'date' : 'text'}
                  value={String(value ?? '')}
                  maxLength={500}
                  onChange={(event) => set(event.target.value)}
                  className={field.type === 'DATE' ? 'w-44' : undefined}
                />
              )}
            </Field>
          );
        })}
      </form>
    </Modal>
  );
}
